import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import API from '../config';
import { Helmet } from 'react-helmet-async';

export default function SetupNickname() {
  const [username, setUsername]   = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [checking, setChecking]   = useState(false);
  const [available, setAvailable] = useState(null);
  const { token, updateUser }     = useAuth();
  const { T }                     = useTheme();
  const navigate                  = useNavigate();

  const randomSuggestion = () => {
    const adj  = ['sharp', 'bold', 'quick', 'smart', 'cool', 'top'];
    const noun = ['predictor', 'oracle', 'prophet', 'analyst', 'pundit', 'sage'];
    return `${adj[Math.floor(Math.random() * adj.length)]}_${noun[Math.floor(Math.random() * noun.length)]}${Math.floor(Math.random() * 999)}`;
  };
  const [suggestions] = useState(() => [randomSuggestion(), randomSuggestion(), randomSuggestion()]);

  const handleCheck = async (val) => {
    const cleaned = val.trim().toLowerCase().replace(/\s+/g, '_');
    setUsername(cleaned);
    setAvailable(null);
    setError('');
    if (cleaned.length < 3) return;
    setChecking(true);
    try {
      const res = await axios.post(`${API}/api/auth/check-username`, { username: cleaned });
      setAvailable(res.data.available);
    } catch (_) {}
    finally { setChecking(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || username.length < 3 || available !== true) return;
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/set-username`, { username }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateUser({ username });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set username');
    } finally { setLoading(false); }
  };

  const borderColor = username.length >= 3
    ? available === true ? T.yes : available === false ? T.no : T.border
    : T.border;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', 'Segoe UI', sans-serif", color: T.text }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input:focus { border-color: ${T.accent} !important; outline: none; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <nav style={{ height: 56, display: 'flex', alignItems: 'center', padding: '0 24px', borderBottom: `1px solid ${T.border}` }}>
        <svg width="140" height="38" viewBox="0 0 160 38" fill="none">
          <rect x="1" y="1" width="36" height="36" rx="9" fill="#131416" stroke="#2a2b2f" strokeWidth="1.5"/>
          <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="3" strokeLinecap="round"/>
          <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="3" strokeLinecap="round"/>
          <text x="46" y="20" dominantBaseline="middle" fontFamily="'DM Sans',sans-serif" fontWeight="800" fontSize="18" fill={T.text} letterSpacing="-1">Mino Markets</text>
        </svg>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: T.textDim, fontFamily: "'DM Mono', monospace" }}>
          Setup
        </span>
      </nav>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 16px', animation: 'fadeIn 0.3s ease' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>

          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎭</div>
            <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', marginBottom: 8 }}>Pick your nickname</h1>
            <p style={{ color: T.textMuted, fontSize: 14, lineHeight: 1.6 }}>This is how other predictors will see you on Mino. Choose wisely!</p>
          </div>

          {error && (
            <div style={{ background: `${T.no}14`, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 14px', borderRadius: 7, marginBottom: 20, fontSize: 13 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7 }}>Username</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.textMuted, fontSize: 14, pointerEvents: 'none' }}>@</span>
                <input
                  type="text"
                  value={username}
                  onChange={e => handleCheck(e.target.value)}
                  placeholder="your_nickname"
                  required
                  maxLength={30}
                  style={{ width: '100%', background: T.surface, border: `1px solid ${borderColor}`, borderRadius: 8, padding: '12px 40px 12px 28px', color: T.text, fontSize: 14, fontFamily: 'inherit', transition: 'border-color 0.2s', outline: 'none' }}
                />
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12 }}>
                  {checking
                    ? <span style={{ color: T.textMuted }}>...</span>
                    : username.length >= 3 && available === true  ? <span style={{ color: T.yes }}>✓</span>
                    : username.length >= 3 && available === false ? <span style={{ color: T.no }}>✗</span>
                    : null}
                </span>
              </div>
              <p style={{ color: T.textDim, fontSize: 11, marginTop: 6 }}>Letters, numbers, underscores only · 3–30 characters</p>
            </div>

            <div>
              <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Suggestions</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {suggestions.map((s, i) => (
                  <button key={i} type="button" onClick={() => handleCheck(s)}
                    style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: '5px 10px', color: T.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: "'DM Mono', monospace", transition: 'all 0.1s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.text; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}>
                    @{s}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !username || username.length < 3 || available !== true}
              style={{ background: loading || !username || username.length < 3 || available !== true ? T.surfaceHi : T.accent, color: loading || !username || username.length < 3 || available !== true ? T.textMuted : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: loading || !username || username.length < 3 || available !== true ? 'not-allowed' : 'pointer', fontSize: 14, transition: 'all 0.15s' }}>
              {loading ? 'Setting nickname...' : 'Continue →'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              style={{ background: 'transparent', border: 'none', color: T.textDim, fontSize: 13, cursor: 'pointer', padding: 4 }}>
              Skip for now
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}